import React, { useState, useEffect, useRef } from "react";
import { Card, Col, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import { MutantApiService } from "../../../../services/MutantApiService";
import IsValidName from "../../../../utils/validators/IsValidName";
import Loading from "../../../home/components/Loading";
import { AdminTitle } from "../../components/AdminTitle";
import FormActions from "../../components/FormActions";
import { InputField } from "../../components/InputField";
import store from "../../../../store/store";
import { validateCsv } from "./PromptsFormFileValidator";
import { getVcSpeakers, fetchData, getFormData, afterSubmit } from "./PromptsFormController";
import { providerSupportsPresets } from "../../../home/hooks/useDemoForm";
import QualityEnum, { getQualityOptions } from "../../../../enums/QualityEnum";

const initialState = {
	name: "",
	speaker: "",
	text: "",
	compression: "",
	file: "",
	vcSpeaker: "",
	isLoading: false,
	isEditing: false,
	userLogged: null,
	selectedFile: null,
	formIsValid: false,
	isEdit: false,
	providers: [],
	models: [],
	vcSpeakers: [],

	presets: [],
	loadingPresets: false,
	preset: null
};

const PromptsForm = props => {
	const fileInput = useRef(null);
	const { auth } = store.getState();

	const [state, setState] = useState({
		...initialState,
		isEditing: !!props.match?.params.id,
		userLogged: auth.user
	});

	const api = new MutantApiService();

	const resolvePresetIdentifier = preset => preset?.id ?? preset?.name ?? preset?.presetId;

	const presetOptions = (state.presets || []).map(preset => {
		const value = resolvePresetIdentifier(preset) ?? null;
		const volume = preset.volume ?? "—";
		const silenceEdgeMs = preset.silenceEdgeMs ?? "—";
		return {
			value,
			label: `${preset.name ?? value} (Volume: ${volume}, Silence: ${silenceEdgeMs}ms)`
		};
	});

	const providerFromSelectedModel = (state.models || []).find(m => (m.name ?? m.model ?? '') === state?.speaker)?.provider ?? null;

	const selectedProviderObj = (state.providers || []).find(
		(p) => (p.name || '').toString() === (providerFromSelectedModel || '').toString()
	);

	const disableTextInput =
		!!state.selectedFile || (!!selectedProviderObj && !selectedProviderObj.default && selectedProviderObj.type !== "EXTERNAL");

	useEffect(() => {
		fetchData(props, state, setState);
	}, []);

	useEffect(() => {
		let canceled = false;
		const providerFromModel = (state.models || []).find(m => (m.name ?? m.model ?? '') === state?.speaker)?.provider ?? null;

		if (!providerFromModel) {
			setState(prev => ({ ...prev, presets: [], preset: null, loadingPresets: false }));
			return () => { canceled = true; };
		}

		if (providerSupportsPresets(providerFromModel)) {
			setState(prev => ({ ...prev, loadingPresets: true }));
			api.makeHttpRequest({ url: "/presets?perPage=100" })
				.then(resp => {
					console.log('[PromptsForm] fetched presets response:', resp);
					if (!canceled) {
						const presetsArr = Array.isArray(resp)
							? resp
							: Array.isArray(resp?.data)
								? resp.data
								: [];
						setState(prev => ({ ...prev, presets: presetsArr }));

						if (state?.preset && typeof state.preset === "string") {
							const found = presetsArr.find(p => resolvePresetIdentifier(p) === state.preset);
							if (found) {
								setState(prev => ({ ...prev, preset: { value: resolvePresetIdentifier(found), label: `${found.name ?? resolvePresetIdentifier(found)} (Volume: ${found.volume ?? "—"}, Silence: ${found.silenceEdgeMs ?? "—"}ms)` } }));
							}
						}
					}
				})
				.catch(err => {
					console.error("Error fetching presets:", err);
					if (!canceled) setState(prev => ({ ...prev, presets: [] }));
				})
				.finally(() => !canceled && setState(prev => ({ ...prev, loadingPresets: false })));
		} else {
			setState(prev => ({ ...prev, presets: [], preset: null }));
		}

		return () => { canceled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.speaker, state.models]);

	const submit = async e => {
		e.preventDefault();

		setState(prevState => ({ ...prevState, isLoading: true }));
		try {
			const baseEndpoint = "/prompts";
			console.log("ID: ", props.match.params.id);
			const reqBase = {
				method: state.isEditing ? "PATCH" : "POST",
				url: state.isEditing
					? `${baseEndpoint}/${props.match.params.id}`
					: baseEndpoint,
				headers: {
					"Content-Type": "multipart/form-data"
				}
			};
			let requestData = {};
			if (state.isEditing) {
				requestData = {
					provider: state?.provider ?? (state.models || []).find(m => (m.name ?? m.model ?? '') === state?.speaker)?.provider ?? null,
					speaker: state.speaker,
					compression: state.compression,
					text: state.text,
					vcSpeaker: state.vcSpeaker,
					presetId: state?.preset?.value ?? null
				};
			} else {
				requestData = await getFormData(state, props);
			}

			console.log("Request data:", requestData);

			await api.makeHttpRequest({
				...reqBase,
				data: requestData
			});

			await afterSubmit(setState, initialState, props.history);
		} catch (e) {
			Swal.fire(
				"Erro!",
				e?.response?.data
					? e.response.data.message
					: "Erro ao criar o prompt, tente novamente mais tarde.",
				"error"
			);
		}

		setState(prevState => ({ ...prevState, isLoading: false }));
	};

	const handleChange = async e => {
		const target = e.target;

		if (!target) return;

		if (target?.name === "file") {
			const result = await validateCsv(target.files[0]);

			if (result.isValid) {
				setState(prevState => ({
					...prevState,
					selectedFile: result.cleanedFile,
					text: "",
					name: ""
				}));
			} else {
				target.value = "";
				setState(prevState => ({
					...prevState,
					selectedFile: null
				}));
			}

			return;
		}

		if (target?.name === "text") {
			setState(prevState => ({
				...prevState,
				selectedFile: null,
				[target.name]: target.value
			}));

			return;
		}

		if (target) {
			setState(prevState => ({
				...prevState,
				[target.name]: target.value
			}));
		}
	};

	const onCleanForm = async () => { };

	window.setPageTitle("Cadastro de Prompt - Admin");

	return (
		<>
			<Loading isLoading={state?.isLoading} />
			<AdminTitle title={"Dados do prompt"} disableHeader />
			<Card className="mt-4 pt-5 px-4 pb-2">
				<Form style={{ width: "100%" }}>
					{/* row 1 - Provider e Modelo */}
					<Form.Row>
						<Form.Group as={Col} lg="3" xs="12">
							<InputField
								type="text"
								as="select"
								name="speaker"
								value={state?.speaker ?? ""}
								label="Voz"
								onChange={async (e) => {
									const value = e.target.value;
									setState(prev => ({ ...prev, speaker: value, preset: null }));

									const provider = (state.models || []).find(m => (m.name ?? m.model ?? '') === value)?.provider ?? null;
									if (provider) {
										await getVcSpeakers(provider, setState);
									} else {
										setState(prev => ({ ...prev, vcSpeakers: [] }));
									}
								}}
								isInvalid={state?.speaker === ""}
								disabled={!state?.models || (state?.models || []).length === 0}
								required
							>
								<option value={""}>Escolha o modelo de voz</option>
								{(state?.models ?? []).map((model, idx) => {
									const value = typeof model === 'string' ? model : model.name;
									return (
										<option key={idx} value={value}>
											{value}
										</option>
									);
								})}
							</InputField>
						</Form.Group>
					</Form.Row>

					{/* row 2 - Nome e Compressão */}
					{state?.speaker ? (
						<Form.Row>
							{/* Nome */}
							{!state.isEditing && (
								<Form.Group as={Col} lg="3" xs="12">
									<InputField
										type="text"
										name="name"
										label="Nome"
										placeholder="Digite o nome"
										value={state?.name ?? ""}
										onChange={handleChange}
										isInvalid={state?.name && !IsValidName(state?.name)}
										disabled={!!state?.selectedFile}
									/>
								</Form.Group>
							)}


							<Form.Group as={Col} lg="3" xs="12">
								<InputField
									type="text"
									as="select"
									name="compression"
									label="Compressão"
									value={state?.compression ?? ""}
									onChange={handleChange}
									isInvalid={state?.compression === ""}
									required
								>
									<option value={""}>Escolha um tipo de compressão</option>
									<option value={QualityEnum.HIGH_QUALITY}>Alta qualidade</option>
									<option value={QualityEnum.URA}>Telefonia (URA)</option>
									<option value={QualityEnum.CCITT}>Telefonia (CCITT)</option>
								</InputField>
							</Form.Group>

							{providerSupportsPresets(providerFromSelectedModel) && (
								<>
									<Form.Group as={Col} lg="3" xs="12">
										<InputField
											type="text"
											as="select2"
											name="preset"
											label="Configuração (Preset)"
											placeholder="Selecione um preset"
											value={state.preset}
											onChange={option => setState(prev => ({ ...prev, preset: option }))}
											disabled={!providerFromSelectedModel || state.loadingPresets}
											options={presetOptions}
										/>
									</Form.Group>
								</>
							)}

							{/* Arquivo */}
							{!state.isEditing && (
								<Form.Group as={Col} lg="3" xs="12">
									<InputField
										ref={fileInput}
										type="file"
										name="file"
										label="Arquivo"
										isClearable={true}
										accept=".csv"
										onChange={handleChange}
										disabled={state?.text?.length}
									/>
								</Form.Group>
							)}
						</Form.Row>
					) : null}

					{/* row 3 - Texto */}
					<Form.Row>
						<Form.Group as={Col} lg="12" xs="12">
							<InputField
								maxLength={2000}
								as="textarea"
								rows="2"
								name="text"
								label="Texto"
								placeholder="Texto a ser sintetizado"
								value={state?.text ?? ""}
								onChange={handleChange}
								isInvalid={state?.text && state?.text?.length < 1}
								disabled={disableTextInput}
							/>
						</Form.Group>
					</Form.Row>
				</Form>
			</Card >
			{state?.speaker && (
				<FormActions
					module={`projects`}
					formIsValid={
						(!!providerFromSelectedModel &&
							!!state?.name &&
							!!state?.text &&
							!!(state?.compression !== "") &&
							!!state?.speaker) ||
						(!!providerFromSelectedModel &&
							!!state?.selectedFile &&
							!!(state?.compression !== "") &&
							!!state?.speaker)
					}
					onCleanForm={onCleanForm}
					onSubmit={submit}
					hideReload
				/>
			)
			}
		</>
	);
};

export default PromptsForm;
